import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';

import { FieldTable2 } from 'components/_basic/Form/FieldTable2';
import { RadioControlled } from 'components/_basic/Form/Radio';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { CurrencyUnitSelector } from 'components/BusinessToolForm/components/CurrencyUnitSelector/CurrencyUnitSelector';
import { useGetPeriodOptions } from 'components/BusinessToolForm/constants/periods';
import { FullScreenWrapper } from 'components/BusinessToolForm/Sections/FinancialPlan/components/FullScreenWrapper';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
  GLOBAL_PRODUCTS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ISalesPlanProductTableProps } from './types';
import { prefix, SALES_NAMES } from '../../../../utils/form';

import styles from './SalesPlanProductTable.module.scss';

export const SalesPlanProductTable: React.FC<ISalesPlanProductTableProps> = ({
  tableName,
  fields,
  disabled = false,
}) => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const periodOptions = useGetPeriodOptions();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const tableTotalName = concatWithDot(prefix, SALES_NAMES.PLANNED_PRODUCTS_VOLUME_TOTAL);
  const titleWatchName = concatWithDot(GLOBAL_PRODUCTS_NAME, ':index', PRODUCT_NAMES.NAME);

  const columnsNames = getPeriodColumnsNames(period) ?? [];
  return (
    <FormSectionCard disabled={disabled} title={t('marketingAndSales.sales.salesPlanTableTitle')} collapsible>
      <FullScreenWrapper title={t('marketingAndSales.sales.salesPlanTableTitle')} disabled={disabled}>
        <Box className={styles.header}>
          <RadioControlled
            id="salesPlanProductTable.period"
            name={GLOBAL_PERIOD_NAME}
            control={control}
            options={periodOptions}
            disabled={disabled}
          />
          <CurrencyUnitSelector name={concatWithDot(prefix, SALES_NAMES.CURRENCY)} disabled={disabled} />
        </Box>

        <FormSectionFilledCard>
          <FieldTable2
            showSumRow
            firstColumnWidth={110}
            rows={fields}
            name={tableName}
            disabled={disabled}
            headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
            totalName={tableTotalName}
            columnsNames={columnsNames ?? []}
            watchRowTitleName={titleWatchName}
            readOnly
          />
        </FormSectionFilledCard>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
